import React from 'react';
import {
  XMarkIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  CreditCardIcon,
  ClipboardDocumentCheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

function TermsContent() {
  return (
    <div className="space-y-4">

      {/* Important Notices */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="bg-red-100 p-1.5 rounded-lg">
            <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
          </div>
          <h3 className="font-bold text-red-800 text-sm">Important Notices</h3>
        </div>
        <ul className="space-y-2.5 text-sm text-red-700 leading-relaxed">
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
            Please notify us on WhatsApp (8080517517) immediately after booking. Payment confirmation is required to approve your reservation. Failure to notify may result in cancellation without a refund.
          </li>
        </ul>
      </div>

      {/* Refund & Court Timing */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-amber-100 p-1.5 rounded-lg">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
            </div>
            <h3 className="font-bold text-amber-800 text-sm">Refund Policy</h3>
          </div>
          <p className="text-sm text-amber-700 leading-relaxed">
            Refunds are applicable only if the slot is cancelled <span className="font-bold">8 hours or more</span> before the scheduled time. No refunds will be provided for late cancellations.
          </p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-amber-100 p-1.5 rounded-lg">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
            </div>
            <h3 className="font-bold text-amber-800 text-sm">Court Timing</h3>
          </div>
          <p className="text-sm text-amber-700 leading-relaxed">
            Players are kindly requested to vacate the court promptly at the end of their booked slot. Play beyond the reserved time is <span className="font-bold">strictly not permitted</span>.
          </p>
        </div>
      </div>

      {/* Membership */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="bg-green-100 p-1.5 rounded-lg">
            <UserGroupIcon className="h-4 w-4 text-green-600" />
          </div>
          <h3 className="font-bold text-gray-800 text-sm">Membership</h3>
        </div>
        <ul className="space-y-2.5 text-sm text-gray-600 leading-relaxed">
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
            Monthly and yearly membership holders may book slots between 5:00 AM – 9:00 AM and 6:00 PM – 9:00 PM.
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
            All users must hold an active membership or register as a guest to access BBC facilities for any sports-related activity.
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
            BBC reserves the right to restrict facility access if any membership fees remain unpaid.
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
            At least one member must be present on each court throughout the booked duration. A single member may not reserve a court without additional member players.
          </li>
        </ul>
      </div>

      {/* Booking & Payment */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="bg-blue-100 p-1.5 rounded-lg">
            <CreditCardIcon className="h-4 w-4 text-blue-600" />
          </div>
          <h3 className="font-bold text-gray-800 text-sm">Booking & Payment</h3>
        </div>
        <ul className="space-y-2.5 text-sm text-gray-600 leading-relaxed">
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
            All applicable fees must be settled prior to accessing the facilities.
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
            BBC reserves the right to limit the number of guests and court rental players at any given time.
          </li>
        </ul>
      </div>

      {/* Facility Rules */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="bg-amber-100 p-1.5 rounded-lg">
            <ClipboardDocumentCheckIcon className="h-4 w-4 text-amber-600" />
          </div>
          <h3 className="font-bold text-gray-800 text-sm">Facility Rules</h3>
        </div>
        <ul className="space-y-2.5 text-sm text-gray-600 leading-relaxed">
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
            All users are required to check in at the front desk using the biometric or electronic system provided.
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
            Only water is permitted on court areas. No food or other beverages are allowed. Please dispose of any items immediately if brought onto the court.
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
            Non-players, including children, are not permitted on the court at any time.
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
            Smoking, spitting, chewing gum, animals, and weapons are strictly prohibited inside the facility.
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
            No external professional coaching activities are permitted. Only BBC-authorised coaching is allowed.
          </li>
        </ul>
      </div>

      {/* General */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="bg-gray-100 p-1.5 rounded-lg">
            <InformationCircleIcon className="h-4 w-4 text-gray-500" />
          </div>
          <h3 className="font-bold text-gray-800 text-sm">General</h3>
        </div>
        <ul className="space-y-2.5 text-sm text-gray-600 leading-relaxed">
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
            BBC shall not be held responsible for any vehicles or damage to vehicles within the parking area.
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
            Our staff may communicate additional rules, updates, or changes from time to time. We appreciate your cooperation.
          </li>
        </ul>
      </div>

    </div>
  );
}

function TermsModal({ isOpen, onClose, showAccept, termsAccepted, onAcceptChange, onConfirm, loading }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-50 rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl animate-smooth-rise" onClick={e => e.stopPropagation()}>

        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 pt-5 pb-7 rounded-t-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg flex-shrink-0">
                <ShieldCheckIcon className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-white leading-tight">Terms & Conditions</h2>
                <p className="text-green-200 text-xs mt-0.5 leading-tight">Best Badminton Club</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0 ml-3"
            >
              <XMarkIcon className="h-5 w-5 text-white/80" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          <TermsContent />
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 bg-white rounded-b-2xl">
          {showAccept ? (
            <>
              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => onAcceptChange(e.target.checked)}
                  className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500 cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700">
                  I have read all conditions and agree to book
                </span>
              </label>
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  disabled={!termsAccepted || loading}
                  className={`px-5 py-2.5 text-sm font-semibold rounded-xl transition-all ${
                    termsAccepted && !loading
                      ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {loading ? 'Processing...' : 'Proceed with Booking'}
                </button>
              </div>
            </>
          ) : (
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-semibold bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors shadow-sm"
              >
                Got it
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TermsModal;
export { TermsContent };
